import { getGridStringOperators, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import getForecastMonthList from "../../../tools/forecast/getForecastMonthList";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../../redux/reducers";
import { useParams } from "react-router";
import { ITNS } from "../../../interface/tns";
import getCompanyData from "../../../tools/forecast/getCompanyData";
import { setTotalByMonth } from "../../../redux/actions/forecastActions";
import CustomDataGrid from "../../customDataGrid/CustomDataGrid";

const CompanyData = ({
    forecastVersion,
    forecastDates,
    selectedCompany,
    setSelectedCompany,
    openModal,
    showDeleteDialog,
    setEditId,
    setOpenModal,
    searchLabel // <-- Ajout de la prop ici
}: {
    forecastVersion: number | undefined,
    forecastDates: string,
    selectedCompany: string[],
    setSelectedCompany: Function,
    openModal: boolean,
    showDeleteDialog: boolean,
    setEditId: Function,
    setOpenModal: Function,
    searchLabel: string // <-- Et ici
}) => {
    let dispatch = useDispatch();
    let token = useSelector((state: RootState) => state.user.token);
    const [error, setError] = useState("");
    const params = useParams();
    const [companyData, setCompanyData] = useState<any[]>([]);
    
    const monthList = getForecastMonthList(forecastDates);

    useEffect(() => {
        getCompanyData(params.id, forecastVersion, setCompanyData, token, dispatch, setError);
    }, [forecastVersion, openModal, showDeleteDialog]);

    // --- COLONNES ---
    const columns: GridColDef[] = useMemo(() => [
        {
            field: "label",
            headerName: "Libellé",
            filterable: true,
            minWidth: 200,
            filterOperators: getGridStringOperators().filter((operator) => operator.value === "contains"),
            renderCell: (params) => (
                <div style={{ whiteSpace: 'normal', fontWeight: params.id === 'total' ? 'bold' : 'normal' }}>
                    {params.value?.split('\n').map((line: any, index: any) => (
                        <span key={index}>{line}<br /></span>
                    ))}
                </div>
            ),
        },
        ...monthList.map((column) => ({
            field: column.field,
            headerName: column.name,
            minWidth: 160,
            align: "right" as const,
            headerAlign: "right" as const,
        }))
    ], [monthList]);

    // --- LOGIQUE DE FILTRAGE ET CALCUL DES LIGNES ---
    const rows = useMemo(() => {
        // 1. On filtre d'abord les données brutes
        const filteredData = companyData.filter((item) =>
            item.label.toLowerCase().includes(searchLabel.toLowerCase())
        );

        // 2. On transforme les données filtrées pour le tableau
        const turnoverRows = filteredData.map((tns: ITNS) => {
            const row: any = {
                id: tns._id,
                label: `\n${tns.label}\n`,
            };

            // On définit months comme un objet indexable pour éviter l'erreur TS7015
            const months = tns.months as Record<string, any>;

            monthList.forEach((m) => {
                // On cherche la valeur soit dans l'objet (si c'est une liste d'objets), soit directement par la clé
                const monthEntry = Object.values(months || {}).find((e: any) => e.label === m.field) as any;
                
                // Utilisation de la variable months typée pour l'accès dynamique
                const val = monthEntry?.value ?? months[m.field] ?? 0;
                
                row[m.field] = `\n${Number(val).toLocaleString()} €\n`;
            });

            return row;
        });

        // 3. Calcul du total sur les données filtrées uniquement
        const totals: any = {};
        monthList.forEach((m) => {
            const sum = filteredData.reduce((acc, data) => {
                const months = data.months as Record<string, any>;
                const monthEntry = Object.values(months || {}).find((e: any) => e.label === m.field) as any;
                const val = monthEntry?.value ?? months[m.field] ?? 0;
                return acc + (Number(val) || 0);
            }, 0);
            totals[m.field] = sum;
        });

        // 4. Mise à jour Redux
        dispatch(setTotalByMonth("Entreprise", totals));

        const totalRow = {
            id: "total",
            label: `\nTOTAL\n`,
            ...Object.fromEntries(
                Object.entries(totals).map(([key, value]) => [key, `\n${Number(value).toLocaleString()} €\n`])
            )
        };

        return filteredData.length > 0 ? [...turnoverRows, totalRow] : [];
    }, [companyData, searchLabel, monthList, dispatch]);
    return (
        <CustomDataGrid
            disableRowSelectionOnClick={true}
            rows={rows}
            columns={columns}
            getRowHeight={() => 'auto'}
            onCellClick={(params) => { 
                if (params.field !== "__check__" && params.row.id !== 'total') { 
                    setEditId(params.row.id); 
                    setOpenModal(true); 
                } 
            }}
            checkboxSelection
            rowSelectionModel={selectedCompany}
            onRowSelectionModelChange={(selectionModel) => { setSelectedCompany(selectionModel) }}
            isRowSelectable={(params) => params.row.id !== 'total'}
            sx={{
                '& .MuiDataGrid-row[data-id="total"]': {
                    bgcolor: '#f1f5f9 !important',
                    fontWeight: 'bold',
                }
            }}
        />
    );
}

export default CompanyData;